import { Button } from '@/components/Button';
import useCandidateMailComposer, { type MailDestination } from '../hooks/useCandidateMailComposer';
import useCandidateRecipientSelection from '../hooks/useCandidateRecipientSelection';
import type { CandidateRecipient } from '../lib/recipients';
import CandidateMailComposeDialogs from './CandidateMailComposeDialogs';
import MailDialog from './MailDialog';
import MailRecipientField from './MailRecipientField';
import MailSubjectBodyFields from './MailSubjectBodyFields';
import TemplateInsertMenu from './TemplateInsertMenu';
import VariableInsertMenu from './VariableInsertMenu';

type CandidateMailComposeModalProps = {
  initialRecipients: CandidateRecipient[];
  excludedCount: number;
  onClose: () => void;
  onFinished: (destination: MailDestination) => void;
};

export default function CandidateMailComposeModal({
  initialRecipients,
  excludedCount,
  onClose,
  onFinished,
}: CandidateMailComposeModalProps) {
  const recipientSelection = useCandidateRecipientSelection(initialRecipients);
  const composer = useCandidateMailComposer({
    recipients: recipientSelection.recipients,
    onFinished,
  });
  const isInteractionLocked = !composer.isSenderConfigured;

  return (
    <>
      <MailDialog
        open={!composer.isFollowUpOpen}
        onClose={onClose}
        preventClose={composer.subject.trim().length > 0 || composer.body.trim().length > 0}
        title="メールを作成"
        widthClassName="w-[1100px]"
        bodyClassName="flex flex-col gap-md p-lg"
        footerStart={
          <div className="flex items-center gap-xs">
            <VariableInsertMenu
              onInsert={(token) => composer.bodyEditorRef.current?.insert(token)}
              disabled={isInteractionLocked}
            />
            <TemplateInsertMenu
              templates={composer.templates}
              emptyMessage={composer.templateMenuMessage}
              onSelect={composer.selectTemplate}
              disabled={isInteractionLocked}
            />
          </div>
        }
        footer={
          <>
            <Button type="button" color="secondary" size="md" onClick={onClose}>
              キャンセル
            </Button>
            <Button
              type="button"
              color="primary"
              size="md"
              disabled={composer.isPreviewDisabled}
              onClick={composer.openPreview}
            >
              プレビューで確認
            </Button>
          </>
        }
      >
        <MailRecipientField
          selection={recipientSelection}
          variant="dropdown"
          disabled={isInteractionLocked}
          note={
            excludedCount > 0
              ? `メールアドレス未登録の${excludedCount}名は宛先から除外しました`
              : null
          }
        />
        <MailSubjectBodyFields
          subject={composer.subject}
          onSubjectChange={composer.setSubject}
          body={composer.body}
          onBodyChange={composer.setBody}
          bodyEditorRef={composer.bodyEditorRef}
          disabled={isInteractionLocked}
          className="h-[498px] shrink-0"
        />
      </MailDialog>
      <CandidateMailComposeDialogs composer={composer} onCancelSenderSetting={onClose} />
    </>
  );
}
